import { useEffect, useRef, useState } from "react";
import { openPanelChat } from "../infrastructure/panel-chat-connection.js";
import { loadAdminChatBooths, mergeBoothContacts, openAdminBoothChat } from "../api/admin-booth-directory.js";
import { mergeHistoryPage } from "../lib/history-page";

/** Keeps panel chat lists and conversations scoped to the current login. */
export default function usePanelChat(token, currentUserId, initialChatId = null) {
  const [messages, setMessages] = useState({ arguments: [null, { code: 200, data: [] }] });
  const [allChats, setAllChats] = useState({});
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [booths, setBooths] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const openingRequest = useRef(null);
  const requestedChatId = useRef(initialChatId);
  const history = useRef(null);
  function requestHistory(connection, entry) {
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      if (history.current !== entry) return;
      history.current = null;
      setError("دریافت تاریخچه کامل نشد؛ دوباره تلاش کنید.");
    }, 15000);
    connection.send(JSON.stringify({ target: "GetMessage", arguments: [{ chatId: entry.chatId, page: entry.page, limit: 100 }] }));
  }

  useEffect(() => { requestedChatId.current = initialChatId; }, [initialChatId]);
  useEffect(() => {
    if (!initialChatId) return;
    const match = messages.arguments[1].data?.find((chat) => Number(chat.id) === initialChatId);
    if (match) setSelected((current) => Number(current.id) === initialChatId ? current : match);
  }, [initialChatId, messages]);

  useEffect(() => {
    const controller = new AbortController();
    setBooths([]);
    setDirectoryLoading(true);
    setOpening(false);
    if (!token) { setDirectoryLoading(false); return; }
    loadAdminChatBooths(token, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setBooths(rows); })
      .catch((reason) => { if (!controller.signal.aborted) setError(reason.message); })
      .finally(() => { if (!controller.signal.aborted) setDirectoryLoading(false); });
    return () => { controller.abort(); openingRequest.current?.abort(); openingRequest.current = null; };
  }, [token, attempt]);

  useEffect(() => {
    if (!token) { setLoading(false); setError("Please sign in to open chat."); return; }
    setLoading(true);
    setConnected(false);
    setError("");
    setAllChats({});
    setSelected({});
    const connection = openPanelChat(process.env.REACT_APP_API_CHAT_URL, token, {
      onReady: () => { setConnected(true); setError(""); },
      onRequestError: (message) => setError(message),
      onError: (message) => {
        clearTimeout(history.current?.timer); history.current = null;
        setConnected(false); setLoading(false); setError(message);
      },
      onFrame: (frame) => {
        const payload = frame.arguments[1];
        if (payload?.code !== 200) {
          clearTimeout(history.current?.timer); history.current = null;
          setLoading(false);
          setError(payload?.message || "Chat request failed.");
          return;
        }
        if (frame.target === "GetAll") {
          setMessages(frame);
          setLoading(false);
          setSelected((current) => current?.id
            ? { ...current, ...payload.data?.find((chat) => chat.id === current.id) }
            : requestedChatId.current ? payload.data?.find((chat) => Number(chat.id) === requestedChatId.current) || {} : payload.data?.[0] || {});
        } else if (frame.target === "GetMessage") {
          const chatId = payload.chatId;
          if (payload.refresh === true) {
            if (!Array.isArray(payload.data) || payload.data.some((row) => Number(row.chatId) !== Number(chatId) || row.id == null)) {
              setError("پاسخ پیام‌ها متعلق به این گفتگو نیست."); return;
            }
            if (Number(history.current?.chatId) === Number(chatId)) {
              history.current.rows = mergeHistoryPage(history.current.rows, payload.data, chatId, Infinity).data;
            }
            setAllChats((current) => ({ ...current, [chatId]: { ...payload,
              data: mergeHistoryPage(current[chatId]?.data || [], payload.data, chatId, Infinity).data } }));
            setError("");
            return;
          }
          const entry = history.current;
          if (!entry || Number(chatId) !== Number(entry.chatId)) return;
          clearTimeout(entry.timer);
          try {
            const result = mergeHistoryPage(entry.rows, payload.data, chatId);
            entry.rows = result.data;
            setAllChats((current) => ({ ...current, [chatId]: { ...payload, data: result.data } }));
            if (result.hasMore) { entry.page++; requestHistory(connection, entry); }
            else history.current = null;
          } catch (reason) { history.current = null; setError(reason.message); }
        } else if (frame.target === "SendMessage") {
          const message = payload.data;
          if (!message?.chatId) return;
          if (Number(history.current?.chatId) === Number(message.chatId)) {
            history.current.rows = [message, ...history.current.rows.filter((item) => item.id !== message.id)];
          }
          setAllChats((current) => {
            const conversation = current[message.chatId] || { data: [] };
            if (conversation.data.some((item) => item.id === message.id)) return current;
            return { ...current, [message.chatId]: { ...conversation, data: [message, ...conversation.data] } };
          });
          connection.send(JSON.stringify({ target: "GetAll", arguments: [] }));
        } else if (frame.target === "CreateChat") {
          setSelected((current) => current?.id ? current : payload.data || {});
        }
      },
    });
    setSocket(connection);
    return () => { clearTimeout(history.current?.timer); history.current = null; connection.close(); };
  }, [token, attempt]);

  useEffect(() => {
    clearTimeout(history.current?.timer);
    history.current = null;
    if (selected?.id && socket) {
      const entry = { chatId: selected.id, page: 1, rows: [] };
      history.current = entry;
      requestHistory(socket, entry);
    }
    return () => { clearTimeout(history.current?.timer); history.current = null; };
    // Each selection owns a finite, sequential history request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, socket]);

  async function selectContact(contact) {
    if (openingRequest.current || !contact.canChat) return;
    if (contact.id) { setSelected(contact); return contact; }
    const controller = new AbortController();
    openingRequest.current = controller;
    setOpening(true);
    setError("");
    try {
      const chat = await openAdminBoothChat(token, contact, controller.signal);
      if (controller.signal.aborted) return;
      setSelected(chat);
      socket?.send(JSON.stringify({ target: "NotifyChatCreated", arguments: [chat.id] }));
      socket?.send(JSON.stringify({ target: "GetAll", arguments: [] }));
      return chat;
    } catch (reason) {
      if (!controller.signal.aborted) setError(reason.message);
    } finally {
      if (openingRequest.current === controller) { openingRequest.current = null; setOpening(false); }
    }
  }

  const contacts = mergeBoothContacts(booths, messages.arguments[1].data || [], currentUserId);
  return { contacts, allChats, selected, setSelected, selectContact, opening,
    loading: loading || directoryLoading, error, socket, connected,
    retry: () => setAttempt((value) => value + 1) };
}
